import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { EmployeeInfoResponse } from '../types/employeeApiType'
import {
    InboxListParams,
    InboxListResponse,
    NotificationDetailsResponse,
    UpdateNotificationParams,
} from '../types/inboxApiType'

export const employeeApi = createApi({
    reducerPath: 'employeeApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)

            const customCorporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId')
            if (customCorporation) headers.set('X-CORP-CONTEXT', customCorporation)

            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    tagTypes: ['Employee', 'Inbox'],
    endpoints: (builder) => ({
        getEmployeeInfo: builder.query<EmployeeInfoResponse, void>({
            query: () => ({
                url: '/employee/info',
                method: 'GET',
            }),
            providesTags: ['Employee'],
        }),
        getEmployeeSettings: builder.query<{ statusCode: number; data: { settings: { payroll: boolean } } }, void>({
            query: () => ({
                url: '/employee/settings',
                method: 'GET',
            }),
            providesTags: ['Employee'],
        }),
        hideEmployeeApp: builder.mutation<void, void>({
            query: () => ({
                url: '/employee/app/hide',
                method: 'POST',
                body: {},
            }),
            invalidatesTags: ['Employee'],
        }),
        getHomeWidgets: builder.query<
            any,
            { widgets: string[]; fromDate?: string; toDate?: string; page?: number; limit?: number }
        >({
            query: (params) => {
                const searchParams = new URLSearchParams()
                searchParams.append('widgets', params.widgets.join(','))
                if (params.fromDate) searchParams.append('fromDate', params.fromDate)
                if (params.toDate) searchParams.append('toDate', params.toDate)
                if (params.page) searchParams.append('page', params.page.toString())
                if (params.limit) searchParams.append('limit', params.limit.toString())

                return {
                    url: `/employee/home?${searchParams.toString()}`,
                    method: 'GET',
                }
            },
            providesTags: ['Employee'],
        }),
        getInbox: builder.mutation<InboxListResponse, InboxListParams>({
            query: ({ tab, ...body }) => ({
                url: '/employee/notification',
                method: 'POST',
                body,
                params: tab ? { tab } : undefined,
                headers: { 'Response-V2': '1' },
            }),
        }),
        getNotificationDetails: builder.query<
            NotificationDetailsResponse,
            { id: number; tab?: InboxListParams['tab'] }
        >({
            query: ({ id, tab }) => ({
                url: `/employee/notification/${id}`,
                method: 'GET',
                params: tab ? { tab } : undefined,
                headers: { 'Response-V2': '1' },
            }),
        }),
        updateNotification: builder.mutation<
            { statusCode: number; success: boolean; data: UpdateNotificationParams },
            UpdateNotificationParams
        >({
            query: ({ id, tab, ...body }) => ({
                url: `/employee/notification/${id}/update`,
                method: 'POST',
                body,
                params: tab ? { tab } : undefined,
                headers: { 'Response-V2': '1' },
            }),
            invalidatesTags: ['Inbox'],
        }),
        deleteNotification: builder.mutation<{ statusCode: number; success: boolean }, number>({
            query: (id) => ({
                url: `/employee/notification/${id}/delete`,
                method: 'POST',
                body: {},
                headers: { 'Response-V2': '1' },
            }),
            invalidatesTags: ['Inbox'],
        }),
    }),
})

export const {
    useGetEmployeeInfoQuery,
    useGetEmployeeSettingsQuery,
    useHideEmployeeAppMutation,
    useGetHomeWidgetsQuery,
    useLazyGetHomeWidgetsQuery,
    useGetInboxMutation,
    useLazyGetNotificationDetailsQuery,
    useUpdateNotificationMutation,
    useDeleteNotificationMutation,
} = employeeApi
